import { Modal } from "@msuite/ui/modal";
import { DataTable, getFlatRowSelection, getInitialRowSelectionState } from "@msuite/ui/table";
import type {
	GetWarehouseLocationsQuerySchema,
	SelectWarehouseLocationsSchema,
} from "@msuite/utils/schemas";
import { useFormContext } from "react-hook-form";
import { ArticlesTable } from "@/components/stock/articles-table";
import { useWarehouseLocations } from "@/hooks/use-warehouse-locations";
import { columns } from "@/sites/data/warehouse-locations/table-config";

interface SelectWarehouseLocationsFormProps {
	initialWarehouseLocationIds: string[];
	warehouseZoneId?: string;
	query: GetWarehouseLocationsQuerySchema;
}

export function SelectWarehouseLocationsForm({
	initialWarehouseLocationIds,
	warehouseZoneId,
	query,
}: SelectWarehouseLocationsFormProps) {
	const methods = useFormContext<SelectWarehouseLocationsSchema>();

	// What the table offers, which is not what `query` describes: `query` names the thing being
	// assigned to, this names the candidates. Assigning to an article, only locations inside a
	// storage zone are candidates at all — an article stocked anywhere else raises a picking round
	// against a zone nobody picks from, or none at all. Assigning to a zone, every location is a
	// candidate, including the zoneless ones a zone is built out of.
	const { warehouseLocations, isLoading, refetch, queryKey } = useWarehouseLocations(
		query?.articleId ? { storageZonesOnly: "true" } : { warehouseZoneId },
	);

	return (
		<Modal.TableWrapper>
			<DataTable
				data={warehouseLocations}
				columns={columns}
				queryKey={queryKey}
				isLoading={isLoading}
				refetch={refetch}
				persistenceKey="data-warehouse-locations-table"
				onRowSelectionChange={(state) => {
					const warehouseLocationIds = getFlatRowSelection(state);
					methods.setValue("warehouseLocationIds", warehouseLocationIds);
				}}
				initialRowSelectionState={getInitialRowSelectionState(initialWarehouseLocationIds)}
				enableRowSelection={(row) => {
					// The article branch needs no check of its own: the list it selects from is
					// already narrowed to storage zones above.
					if (query?.articleId) return true;
					if (query?.warehouseZoneId) {
						if (!row.original.warehouseZoneId) return true;
						if (row.original.warehouseZoneId === query.warehouseZoneId) return true;
						return false;
					}
					return false;
				}}
				enableMultiRowSelection
				getRowCanExpand={() => true}
				renderSubRows={(row) => (
					<ArticlesTable
						variant="sub-table"
						query={{
							warehouseLocationId: row.original.id,
						}}
					/>
				)}
				menu={null}
			/>
		</Modal.TableWrapper>
	);
}
